import { type Hex, type HexLayout, type PixelPoint, axialToPixel } from '../../core/hex';

/**
 * Player-position marker (HEX-41).
 *
 * Layered "target" glyph drawn on top of the world container at the character's
 * hex. Two concentric rings (outer dark, inner gold) plus a small bright pip
 * give a high-contrast read against every biome tint — dark mountains and lake
 * tones, bright snow plains, mid-tone forest blends. Geometry is centred on the
 * hex's pixel centre; sizes scale with `layout.size` so the marker stays
 * proportionate at every zoom.
 *
 * Pure helpers live here so they can be unit-tested without booting Pixi
 * (the Pixi `Application` itself is hard to spin up under happy-dom).
 */

/** Marker pixel centre. Thin wrapper around `axialToPixel` so callers can ask
 *  the marker module directly rather than reaching for hex maths. */
export function playerMarkerCentre(hex: Hex, layout: HexLayout): PixelPoint {
  return axialToPixel(hex, layout);
}

export interface PlayerMarkerStyle {
  readonly outerRadius: number;
  readonly innerRadius: number;
  readonly pipRadius: number;
  readonly outerColour: number;
  readonly innerColour: number;
  readonly pipColour: number;
  readonly outerWidth: number;
  readonly innerWidth: number;
}

/** Outer ring (dark) + inner ring (gold) + central pip (near-white). The
 *  outer ring is the contrast band against pale biomes; the inner gold ring
 *  reads against the dark mountain / lake tiles. */
export const PLAYER_MARKER_STYLE: PlayerMarkerStyle = {
  outerRadius: 0.55,
  innerRadius: 0.4,
  pipRadius: 0.12,
  outerColour: 0x0c0c10,
  innerColour: 0xf0d264,
  pipColour: 0xf6f6f8,
  outerWidth: 3,
  innerWidth: 2,
};

export interface PlayerMarkerGeometry {
  readonly centre: PixelPoint;
  readonly outerRadius: number;
  readonly innerRadius: number;
  readonly pipRadius: number;
}

export function playerMarkerGeometry(
  hex: Hex,
  layout: HexLayout,
  style: PlayerMarkerStyle = PLAYER_MARKER_STYLE,
): PlayerMarkerGeometry {
  return {
    centre: playerMarkerCentre(hex, layout),
    outerRadius: layout.size * style.outerRadius,
    innerRadius: layout.size * style.innerRadius,
    pipRadius: layout.size * style.pipRadius,
  };
}
